import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Products", href: "#products" },
  { label: "Work", href: "#work" },
  { label: "Sectors", href: "#sectors" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Contact", href: "#contact" }
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-baseline gap-2 group"
        >
          <span className="font-display text-xl md:text-2xl tracking-tight leading-none">
            ATAGASTHYA
          </span>
          <span className="hidden sm:block w-2 h-2 bg-primary group-hover:scale-150 transition-transform" />
          <span className="font-mono-ibm text-[10px] tracking-widest text-muted-foreground hidden md:block">
            PACKAGING
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono-ibm text-xs tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary group-hover:w-full transition-all duration-300" />
            </button>
          ))}
          <button
            data-testid="header-quote-btn"
            onClick={() => go("#contact")}
            className="font-mono-ibm text-xs tracking-widest uppercase bg-primary text-primary-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
          >
            Request Quote
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-t border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="font-mono-ibm text-sm tracking-widest uppercase text-left"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-quote-btn"
                onClick={() => go("#contact")}
                className="font-mono-ibm text-xs tracking-widest uppercase bg-primary text-primary-foreground px-5 py-3 mt-2"
              >
                Request Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
